import Tippy, { TippyProps } from '@tippyjs/react';
import styled from '@emotion/styled';
import { colors, fontSizes, shadows, spacing } from '../utils';
import 'tippy.js/dist/tippy.css';

const TippyParent = styled.span`
  color: ${colors.text.primary};
  font-family: Soleil;

  /* General style overrides */
  .tippy-box {
    background: transparent;
    background-color: transparent;
    border-radius: 0;
    box-shadow: ${shadows.primary};
    font-family: Soleil;
    padding: 0;
    text-align: left;
  }

  /* custom box-shadow transition to remove "white box" effect when animating tooltip entrance */
  /* Shift Away */
  .tippy-box[data-animation='shift-away'][data-state='visible'] {
    transition: box-shadow 325ms cubic-bezier(0.165, 0.84, 0.44, 1) 50ms;
  }

  /* Shift Towards */
  .tippy-box[data-animation='shift-towards'][data-state='visible'] {
    transition: box-shadow 325ms cubic-bezier(0.165, 0.84, 0.44, 1) 50ms;
  }

  [data-tippy-root=''] [data-animation='shift-towards'][data-state='hidden'] {
    box-shadow: none;
    transition: box-shadow 5ms linear;
  }

  /* Scale */
  .tippy-box[data-animation='scale'][data-state='visible'] {
    transition: box-shadow 325ms cubic-bezier(0.165, 0.84, 0.44, 1) 50ms;
  }

  [data-tippy-root=''] [data-animation='scale'][data-state='hidden'] {
    box-shadow: none;
  }

  /* Perspective */
  .tippy-box[data-animation='perspective'][data-state='visible'] {
    transition: box-shadow 325ms cubic-bezier(0.165, 0.84, 0.44, 1) 50ms;
  }

  [data-tippy-root=''] [data-animation='perspective'][data-state='hidden'] {
    box-shadow: none;
  }

  /* Fade */
  .tippy-box[data-animation='fade'][data-state='visible'] {
    transition: box-shadow 325ms cubic-bezier(0.165, 0.84, 0.44, 1) 50ms;
  }

  /* Tooltip Theme */
  .tippy-box[data-theme~='tooltip'] {
    background: ${colors.background.dark};
    background-color: ${colors.background.dark};
    color: ${colors.white};
    font-size: 13px;

    .tippy-content {
      padding: ${spacing.medium};
    }
  }

  /* Tooltip Theme arrow customization */
  .tippy-box[data-theme~='tooltip'][data-placement^='bottom']
    > .tippy-arrow::before {
    border-bottom-color: ${colors.background.dark};
  }

  .tippy-box[data-theme~='tooltip'][data-placement^='left']
    > .tippy-arrow::before {
    border-left-color: ${colors.background.dark};
  }

  .tippy-box[data-theme~='tooltip'][data-placement^='right']
    > .tippy-arrow::before {
    border-right-color: ${colors.background.dark};
  }

  .tippy-box[data-theme~='tooltip'][data-placement^='top']
    > .tippy-arrow::before {
    border-top-color: ${colors.background.dark};
  }

  /* Menu Theme */
  .tippy-box[data-theme~='menu'] {
    background: ${colors.white};
    background-color: ${colors.white};
    color: ${colors.text.primary};
    font-size: ${fontSizes.medium};

    .tippy-content {
      padding: 0;
    }
  }

  /* Menu Theme arrow customization */
  .tippy-box[data-theme~='menu'][data-placement^='bottom']
    > .tippy-arrow::before {
    border-bottom-color: ${colors.white};
  }

  .tippy-box[data-theme~='menu'][data-placement^='left']
    > .tippy-arrow::before {
    border-left-color: ${colors.white};
  }

  .tippy-box[data-theme~='menu'][data-placement^='right']
    > .tippy-arrow::before {
    border-right-color: ${colors.white};
  }

  .tippy-box[data-theme~='menu'][data-placement^='top'] > .tippy-arrow::before {
    border-top-color: ${colors.white};
  }
`;

const Trigger = styled.button`
  background: transparent;
  border: none;
  color: ${colors.text.primary};
  cursor: pointer;
  font-family: Soleil;
  margin: 0;
  outline-color: transparent;
  padding: 0;

  :focus-visible {
    outline: 1px auto ${colors.text.link};
    outline-offset: 1px;
    transition: outline-color 325ms ease-out;
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
`;

const defaultTippyProps: TippyProps = {
  animation: 'fade', // default: shift-away
  appendTo: 'parent', // default () => document.body
  arrow: false,
  content: '',
  ignoreAttributes: false,
  interactive: true, // default false
  maxWidth: 230, // default 350
  placement: 'top',
  role: 'tooltip',
  trigger: 'mouseenter click', // default 'mouseenter focus'
};

export const Popover = (
  props: TippyProps & { type: 'menu' | 'tooltip' }
): JSX.Element => {
  const { children, type = 'menu', ...tippyProps } = props;

  return (
    <TippyParent data-testid="pinpoint-help-icon">
      <Tippy {...defaultTippyProps} {...tippyProps} theme={type}>
        <Trigger type="button">{children}</Trigger>
      </Tippy>
    </TippyParent>
  );
};

Popover.displayName = 'Popover';
